import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { RHero, LyricSong, QuoteBand, RTakeItFurther } from '../components/Resources.jsx';

// Calon Lân MP3s are served from media.<env>.saysomethingin.com.
// `VITE_MEDIA_BASE` is set per-environment by the deploy workflows.
const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE || '';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  rCrumb: 'Calon Lân',
  rKicker: 'Mini course · Free',
  rTitle: 'Calon Lân.',
  rLede: "The Welsh hymn that became a rugby anthem. In two free half-hour lessons, you'll learn the first verse and the chorus – properly, in Welsh, using the SSi method. No notebooks. No flashcards. Just you, a pair of headphones, and your brain doing something remarkable.",

  promiseKicker: "What you'll come away with",
  promiseTitle: 'Two half hours, and the hymn is yours.',
  promise1Title: 'Master the words.',
  promise1Body: "By the end of the second lesson you'll be able to sing the first verse and chorus with understanding – not just the right sounds, but the real meaning sitting inside them.",
  promise2Title: 'Make them part of you.',
  promise2Body: "The SSi method plants the words deep, so you don't just remember them – they belong to you. You'll find them coming back at the match, at the wedding, at the kitchen sink.",
  promise3Title: 'See your brain do something remarkable.',
  promise3Body: 'A lot of people arrive certain they\'re "no good at languages". An hour later they\'re singing Calon Lân in Welsh. It\'s your brain – not a gift, not a knack – and this is how to use it.',

  lessonsKicker: 'The course',
  lessonsTitle: 'Two lessons. Press play.',
  lessonsLede: "Each lesson is about 30 minutes. Find somewhere you can speak out loud – in the car, on a walk, pottering round the house – and say the words along with us. That's the whole method.",
  l1Num: '01',
  l1Badge: '~30 min',
  l1Title: 'Lesson 1 – The first verse.',
  l1Body: "We'll teach you the first verse of Calon Lân, line by line. You'll be asked to say each bit out loud before we do, which feels odd for about ninety seconds and then feels completely normal. Stick with it.",
  l1Src: `${MEDIA_BASE}/welsh/calon-lan/lesson1.mp3`,
  l2Num: '02',
  l2Badge: '~30 min',
  l2Title: 'Lesson 2 – The chorus.',
  l2Body: "Lesson 2 picks up where 1 left off: the chorus – the bit the whole crowd knows – plus a gentle review of the verse. By the end, you'll have the whole first section from memory.",
  l2Src: `${MEDIA_BASE}/welsh/calon-lan/lesson2.mp3`,
  lessonsTip: "Don't worry about making mistakes. Mistakes are how it works. We give you a little more each time – if you get bits wrong, that's fine, keep going.",

  songKicker: 'The lyrics',
  songTitle: 'All three verses, side by side.',
  songSub: "You'll learn verse one and the chorus in the course above. Here are all three verses and the chorus in full, so you can follow along at the match – or at a wedding, or at a funeral, or in the kitchen on a Sunday afternoon.",
  songVerseLabel: 'Verse',
  songTipsTitle: 'Pronunciation notes',
  songTipsBody: "Two Welsh sounds trip most English speakers: ll (as in 'Lân' – and 'lili') is a soft hiss made with the tongue behind the top teeth, and u is closer to an English 'ee' than 'oo'. Don't worry about getting them perfect – sing boldly, blend in with the choir.",

  historyKicker: 'The story of Calon Lân',
  historyTitle: 'A cigarette packet, a pub in Treboeth, and a hymn the whole of Wales knows.',
  historyIntro: 'On 12 March 1881, Cardiff beat Llanelli in the fourth South Wales Challenge Cup final. On the exact same day, ten miles away at the Castle Hotel in Neath, representatives of eleven Welsh clubs met to propose the formation of a Welsh Rugby Football Union. Around 33 years earlier, in a small cottage in Llanelli, a stonemason and his wife had a baby boy they named Daniel.',
  historyP1Heading: 'The poet who was a labourer.',
  historyP1Body: "Daniel James's family were poor, and the untimely death of his father meant Daniel went to work at the Landore Tinplate Works aged 13. He stayed there until the works closed around 1894, then became a miner, and later a cemetery caretaker. He was widowed twice and outlived one of his sons. He was also, it must be said, rather fond of a drink – he had his own chair at the King's Head in Treboeth, and people said he spent a lot of his free hours there, marinated in beer.",
  historyP2Heading: 'The back of a cigarette packet.',
  historyP2Body: "James had another gift. A chapel elder called D.W. Thomas spotted his potential as a poet and taught him the Welsh tradition. James wrote under the pen names Dafydd Mynyddbach and, later, Gwyrosydd – and the pub audiences who bought him beer in exchange for verses kept him in poems (and beer) for years. It's believed he wrote the words to Calon Lân on the back of a cigarette packet, in a tafarn, sometime in the 1890s. The Welsh composer John Hughes set it to music. It's rumoured to have been performed for the first time at the Blaengarw Hotel.",
  historyP3Heading: 'From chapel to Cardiff Arms Park.',
  historyP3Body: "By 1898 three volumes of James's work had been published; Calon Lân was in the second, and spread quickly. Its words and melody are a popular choice for weddings and funerals and gatherings of every sort in Wales, but over the last century it has become something else as well – one of the great rugby anthems, sung by choirs and seventy-odd thousand people at Wales internationals, even when we're losing.",
  historyImg1Caption: 'Wales vs. anyone, Principality Stadium.',
  historyImg2Caption: 'The Landore Tinplate Works, where Daniel James started work at 13.',
  historyImg3Caption: 'Daniel James wrote as "Gwyrosydd" – bard of the Welsh working class.',

  bandKicker: 'What others are saying',
  bandTitle: "A small encouragement, from people who've been here.",
  bandQ1: "SSi is an excellent way to build confidence. It gets you speaking and really works! I'm very grateful to them.",
  bandN1: 'Leanne Wood',
  bandR1: 'Former Leader of Plaid Cymru · SSi Welsh',
  bandPhoto1: 'leanne-wood.jpg',
  bandQ2: "I'm on a four-month trip to Central America and about 95% of the Spanish I'm using here came from SSi.",
  bandN2: 'Rachel Healy',
  bandR2: 'Learner · SSi Spanish · Costa Rica',
  bandPhoto2: 'rachel-healy.png',
  bandQ3: 'The SSi approach flies in the face of established language learning theory and method – and yet, it works.',
  bandN3: 'Prof. Tess Fitzpatrick',
  bandR3: 'Head of Applied Linguistics · Swansea University',
  bandPhoto3: 'tess-fitzpatrick.png',

  rTakeKicker: 'Loved the lessons?',
  rTakeTitle: "That's the method. And there's a whole course of it.",
  rTakeBody: 'Those two half-hour lessons are a real taste of how SSi works. If you liked it, the full course will take you from complete beginner to your first Welsh conversation in about six months – no grammar tables, no written homework, no stress.',
  rTakeCta1: 'See pricing',
  rTakeCta2: 'Try a free sample',
  rTakeQuote: 'As I was once told, languages are not spoken — they are sung.',
  rTakeQuoteName: 'Erin',
  rTakeQuoteRole: 'SSi Welsh learner',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['rCrumb','rKicker','rTitle','rLede'], long:['rLede'] },
  { name: 'Promises', keys: ['promiseKicker','promiseTitle','promise1Title','promise1Body','promise2Title','promise2Body','promise3Title','promise3Body'], long:['promise1Body','promise2Body','promise3Body'] },
  { name: 'Lessons', keys: ['lessonsKicker','lessonsTitle','lessonsLede','l1Num','l1Badge','l1Title','l1Body','l1Src','l2Num','l2Badge','l2Title','l2Body','l2Src','lessonsTip'], long:['lessonsLede','l1Body','l2Body','lessonsTip'] },
  { name: 'Lyrics', keys: ['songKicker','songTitle','songSub','songVerseLabel','songTipsTitle','songTipsBody'], long:['songSub','songTipsBody'] },
  { name: 'History', keys: ['historyKicker','historyTitle','historyIntro','historyP1Heading','historyP1Body','historyP2Heading','historyP2Body','historyP3Heading','historyP3Body','historyImg1Caption','historyImg2Caption','historyImg3Caption'], long:['historyIntro','historyP1Body','historyP2Body','historyP3Body'] },
  { name: 'Quote band', keys: ['bandKicker','bandTitle','bandQ1','bandN1','bandR1','bandQ2','bandN2','bandR2','bandQ3','bandN3','bandR3'], long:['bandQ1','bandQ2','bandQ3'] },
  { name: 'Take it further', keys: ['rTakeKicker','rTakeTitle','rTakeBody','rTakeCta1','rTakeCta2','rTakeQuote','rTakeQuoteName','rTakeQuoteRole'], long:['rTakeBody','rTakeQuote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const VERSES = [
  { cy: ["Nid wy'n gofyn bywyd moethus,", "Aur y byd na'i berlau mân:", "Gofyn wyf am galon hapus,", "Calon onest, calon lân."],
    en: ["I don't ask for a lavish life,", "for gold, or pearls however small:", "I ask for a happy heart,", "an honest heart, a pure heart."] },
  { cy: ["Calon lân yn llawn daioni,", "Tecach yw na'r lili dlos:", "Dim ond calon lân all ganu –", "Canu'r dydd a chanu'r nos."],
    en: ["A pure heart, full of goodness,", "fairer than the prettiest lily:", "only a pure heart can sing –", "sing by day, and sing by night."] },
  { cy: ["Pe dymunwn olud bydol,", "Hedyn buan ganddo sydd:", "Golud calon lân, rinweddol,", "Yn dwyn bythol elw fydd."],
    en: ["If I wished for worldly riches,", "they would turn to dust as quickly:", "the riches of a pure, virtuous heart", "will bring a profit that never fades."] },
];

const CHIPS = [
  { key:'Format',   val:'Mini course · 2 lessons' },
  { key:'Length',   val:'About 30 min each' },
  { key:'Level',    val:'Complete beginners' },
  { key:'Cost',     val:'Free, always' },
];

/* --- Promise band (three points) --- */
const pr = {
  wrap: { padding:'64px 32px 20px', maxWidth:1080, margin:'0 auto' },
  head: { marginBottom:32, maxWidth:640 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.2, margin:0, letterSpacing:'-.01em', textWrap:'balance' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24 },
  card: { padding:'26px 28px', background:'var(--offWhite, #FDF5F5)', borderRadius:16, border:'1px solid var(--border)' },
  num: { fontFamily:'var(--font-display)', fontSize:22, color:'var(--brandRed)', lineHeight:1, marginBottom:14 },
  cardTitle: { fontFamily:'var(--font-display)', fontSize:20, margin:'0 0 10px', letterSpacing:'-.005em' },
  cardBody: { fontSize:15, lineHeight:1.6, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
};
function PromiseBand({ t }) {
  const items = [
    { n:'01', title:t.promise1Title, body:t.promise1Body },
    { n:'02', title:t.promise2Title, body:t.promise2Body },
    { n:'03', title:t.promise3Title, body:t.promise3Body },
  ];
  return (
    <section style={pr.wrap}>
      <header style={pr.head}>
        <div style={pr.kicker}>{t.promiseKicker}</div>
        <h2 style={pr.h2}>{t.promiseTitle}</h2>
      </header>
      <div className="ssi-res__promiseGrid" style={pr.grid}>
        {items.map((it, i) => (
          <article key={i} style={pr.card}>
            <div style={pr.num}>{it.n}</div>
            <h3 style={pr.cardTitle}>{it.title}</h3>
            <p style={pr.cardBody}>{it.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* --- Lesson cards (big audio blocks) --- */
const ls = {
  wrap: { padding:'56px 32px 32px', maxWidth:1080, margin:'0 auto' },
  head: { marginBottom:32, maxWidth:640 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.2, margin:'0 0 10px', letterSpacing:'-.01em' },
  lede: { fontSize:15.5, color:'var(--grey)', margin:0, maxWidth:'62ch', lineHeight:1.6, textWrap:'pretty' },
  grid: { display:'flex', flexDirection:'column', gap:20 },
  card: { background:'#fff', border:'1px solid var(--border)', borderRadius:18, padding:'26px 28px 26px', display:'grid', gridTemplateColumns:'auto 1fr', gap:24, alignItems:'start' },
  num: { fontFamily:'var(--font-display)', fontSize:56, color:'var(--brandRed)', lineHeight:.9, letterSpacing:'-.02em', minWidth:80 },
  body: {},
  meta: { display:'flex', alignItems:'center', gap:10, marginBottom:6 },
  chip: { fontSize:11, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, border:'1px solid var(--brandRed)', padding:'2px 9px', borderRadius:999 },
  title: { fontFamily:'var(--font-display)', fontSize:22, margin:'0 0 10px', letterSpacing:'-.005em' },
  text: { fontSize:15, color:'var(--grey)', lineHeight:1.6, margin:'0 0 18px', textWrap:'pretty', maxWidth:'60ch' },
  audio: { width:'100%', maxWidth:600 },
  tip: { marginTop:8, padding:'20px 24px', background:'var(--offWhite, #FDF5F5)', border:'1px dashed var(--border-strong)', borderRadius:14, fontSize:14.5, lineHeight:1.6, color:'var(--fg-1)', textWrap:'pretty' },
  tipLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginRight:10 },
};
function Lessons({ t }) {
  return (
    <section style={ls.wrap}>
      <header style={ls.head}>
        <div style={ls.kicker}>{t.lessonsKicker}</div>
        <h2 style={ls.h2}>{t.lessonsTitle}</h2>
        <p style={ls.lede}>{t.lessonsLede}</p>
      </header>
      <div style={ls.grid}>
        {[{n:t.l1Num, badge:t.l1Badge, title:t.l1Title, body:t.l1Body, src:t.l1Src},
          {n:t.l2Num, badge:t.l2Badge, title:t.l2Title, body:t.l2Body, src:t.l2Src}].map((l, i) => (
          <article key={i} style={ls.card}>
            <div style={ls.num}>{l.n}</div>
            <div style={ls.body}>
              <div style={ls.meta}>
                <span style={ls.chip}>{l.badge}</span>
              </div>
              <h3 style={ls.title}>{l.title}</h3>
              <p style={ls.text}>{l.body}</p>
              <audio controls preload="metadata" style={ls.audio} src={l.src}>
                Your browser doesn't support audio – <a href={l.src}>download the MP3</a>.
              </audio>
            </div>
          </article>
        ))}
      </div>
      <div style={ls.tip}>
        <span style={ls.tipLabel}>A tiny bit of advice</span>
        {t.lessonsTip}
      </div>
    </section>
  );
}

/* --- History section (long-form copy with 3 image placeholders) --- */
const hi = {
  wrap: { background:'var(--offWhite, #FDF5F5)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', padding:'80px 32px', marginTop:48 },
  inner: { maxWidth:1080, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { fontFamily:'var(--font-display)', fontSize:40, lineHeight:1.1, margin:'0 0 22px', letterSpacing:'-.02em', textWrap:'balance', maxWidth:'24ch' },
  intro: { fontSize:18, lineHeight:1.6, color:'var(--fg-1)', margin:'0 0 56px', maxWidth:'62ch', textWrap:'pretty' },
  body: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'start' },
  col: { display:'flex', flexDirection:'column', gap:40 },
  p: {},
  pHeading: { fontFamily:'var(--font-display)', fontSize:22, margin:'0 0 12px', letterSpacing:'-.005em' },
  pBody: { fontSize:16, lineHeight:1.7, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
  imgWrap: { background:'#fff', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' },
  img: { aspectRatio:'4/5', width:'100%', display:'block', objectFit:'cover' },
  caption: { fontSize:13, color:'var(--grey)', padding:'12px 16px', lineHeight:1.5, borderTop:'1px solid var(--border)' },
};
function HistoryImage({ src, alt, caption }) {
  return (
    <figure style={{margin:0, ...hi.imgWrap}}>
      <img src={src} alt={alt} style={hi.img} loading="lazy"/>
      {caption && <figcaption style={hi.caption}>{caption}</figcaption>}
    </figure>
  );
}
function History({ t }) {
  return (
    <section style={hi.wrap}>
      <div style={hi.inner}>
        <div style={hi.kicker}>{t.historyKicker}</div>
        <h2 style={hi.h2}>{t.historyTitle}</h2>
        <p style={hi.intro}>{t.historyIntro}</p>
        <div className="ssi-res__historyBody" style={hi.body}>
          <div style={hi.col}>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP1Heading}</h3>
              <p style={hi.pBody}>{t.historyP1Body}</p>
            </div>
            <HistoryImage src="/assets/calon-lan/chimneys.jpg" alt="Brick chimneys of a disused tinplate works" caption={t.historyImg2Caption}/>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP3Heading}</h3>
              <p style={hi.pBody}>{t.historyP3Body}</p>
            </div>
          </div>
          <div style={hi.col}>
            <HistoryImage src="/assets/calon-lan/lineout.jpg" alt="A rugby line-out, players leaping for the ball" caption={t.historyImg1Caption}/>
            <div style={hi.p}>
              <h3 style={hi.pHeading}>{t.historyP2Heading}</h3>
              <p style={hi.pBody}>{t.historyP2Body}</p>
            </div>
            <HistoryImage src="/assets/calon-lan/calligraphy.jpg" alt="A hand writing with a calligraphy pen and inkwell" caption={t.historyImg3Caption}/>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function WelshCalonLan() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Calon Lân">
      <Header t={tweaks}/>
      <main>
        <RHero t={tweaks} chips={CHIPS}/>
        <PromiseBand t={tweaks}/>
        <Lessons t={tweaks}/>
        <LyricSong t={tweaks} verses={VERSES} hideAudio={true}/>
        <History t={tweaks}/>
        <QuoteBand t={tweaks}/>
        <RTakeItFurther t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
